<template>
  <v-dialog v-model="editDialog" class="the-app-dialogs" persistent max-width="600px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t(`Edit Statement's Information`) }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout row wrap justify-content-end>
            <v-flex xs6 pa-2>
              <v-text-field
                v-model.number="form.amount"
                :label="`${$t('Amount')} (${systemCurrency}) *`"
                name="amount"
                box
                hide-details
                background-color="white"
                append-icon="fa-hand-holding-usd"
                @click:append="isMultiCurrency = !isMultiCurrency"
                :readonly="isMultiCurrency"
                autocomplete="off"
                :error="errors.has('amount')"
                v-validate="'required|decimal|min_value:0'"
              />
            </v-flex>

            <v-flex xs6 class="pa-2">
              <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
                <v-text-field
                  slot="activator"
                  :value="form.date"
                  :label="`${$t('Date')} *`"
                  prepend-inner-icon="event"
                  box
                  readonly
                  hide-details
                  name="statement_date"
                  background-color="white"
                  autocomplete="off"
                  :error="errors.has('statement_date')"
                  v-validate="'required|date_format:yyyy-MM-dd'"
                />
                <v-date-picker @change="dateMenu = false" v-model="form.date" />
              </v-menu>
            </v-flex>

            <v-flex xs6 sm6 class="pa-2">
              <v-text-field
                v-if="isMultiCurrency"
                v-model="form.amount_main"
                :label="`${$t('Amount')} (${systemCurrency}) *`"
                box
                hide-details
                background-color="white"
                name="amount_main"
                :error="errors.has('amount_main')"
                v-validate="'required|decimal:3|min_value:0'"
                @input="amountCalculation()"
              />
            </v-flex>

            <v-flex xs6 sm6 pr-2 pl-2 pt-2>
              <v-text-field
                v-if="isMultiCurrency"
                v-model="form.amount_secondary"
                :label="`${$t('Amount')} (${systemCurrency === 'USD' ? $t('IQD') : $t('USD')}) *`"
                box
                :hint="`${$t('Dollar Rate')} : ${form.dollar_rate}`"
                persistent-hint
                background-color="white"
                name="amount_secondary"
                :error="errors.has('amount_secondary')"
                v-validate="'required|decimal:3|min_value:0'"
                @input="amountCalculation()"
              />
            </v-flex>

            <v-flex xs12 pa-2>
              <v-autocomplete
                v-model="form.payment_type"
                :label="`${$t('Receive Type')} *`"
                hide-details
                :items="paymentTypes"
                box
                data-vv-name="receive_type"
                background-color="white"
                :error="errors.has('receive_type')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2 v-if="form.payment_type === 'Remittance'">
              <v-autocomplete
                v-model="form.remittance_office_id_fk"
                :label="`${$t('Remittance Office')} *`"
                hide-details
                :items="remittances"
                box
                data-vv-name="Remittance Office"
                background-color="white"
                :error="errors.has('Remittance Office')"
                v-validate="'required'"
              />
            </v-flex>

            <v-flex xs6 pa-2 v-if="form.payment_type === 'Remittance'">
              <v-text-field
                v-model.number="form.remittance_commission"
                :label="`${$t('Remittance Commission')} (${systemCurrency}) *`"
                name="remittance_commission"
                box
                hide-details
                background-color="white"
                autocomplete="off"
                data-vv-delay="1000"
                :error="errors.has('remittance_commission')"
                v-validate="'required|decimal|min_value:0'"
              />
            </v-flex>

            <v-flex xs12 class="pa-2">
              <v-textarea
                v-model="form.note"
                :label="$t('Note')"
                name="statement_note"
                box
                hide-details
                height="80"
                background-color="white"
              />
            </v-flex>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end>
              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :loading="btnUpdateLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Update') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'edit-component',

  props: {
    editDialog: { type: Boolean, required: true },
    accountId: { type: Number, required: true }
  },

  data() {
    return {
      systemCurrency: localStorage.systemCurrency,
      btnUpdateLoading: false,
      dateMenu: false,
      isMultiCurrency: false,
      form: {
        payment_type: '',
        remittance_office_id_fk: null,
        remittance_commission: null,
        amount: 0,
        amount_main: 0,
        amount_secondary: 0,
        dollar_rate: 0,
        date: '',
        cash_or_opening_balance: '',
        note: '',
        attachment: null
      }
    }
  },

  computed: {
    ...mapGetters({
      cashList: 'receivableStatements/getCashOrOpenList',
      dollarRate: 'dollarRates/getLastDollarPrice',
      statementInfo: 'receivableStatements/getStatementInfo',
      paymentTypes: 'accountPayable/getPaymentTypes',
      remittances: 'remittances/getFullRemittanceOfficesList',
      settings: 'configurations/getSystemSettings'
    }),
    hasOpeningBalance() {
      return this.settings.opening_balance_status === 'Active'
    }
  },

  watch: {
    editDialog: {
      immediate: false,
      handler() {
        if (this.editDialog) {
          if (this.hasOpeningBalance) {
            this.paymentTypes.push({ value: 'Opening Balance', text: this.$t('Opening Balance') })
          }
          this.addEscKeyListener()
          this.$validator.pause()
          this.form.statement_id = Number(this.statementInfo.receivable_statement_id)
          this.form.amount = this.statementInfo.amount
          this.isMultiCurrency = this.statementInfo.is_multi_currency === '1'
          this.form.dollar_rate = this.statementInfo.dollar_rate
          if (this.form.dollar_rate === undefined) {
            this.form.dollar_rate = this.dollarRate
          }
          this.form.amount_main = this.statementInfo.amount_main
          this.form.amount_secondary = this.statementInfo.amount_secondary
          this.form.date = this.statementInfo.statement_date
          this.form.cash_or_opening_balance = this.statementInfo.cash_or_opening_balance
          this.form.note = this.statementInfo.statement_not
          this.form.payment_type = this.statementInfo.cash_or_opening_balance
          if (this.statementInfo.remittance_details) {
            this.form.remittance_office_id_fk = this.statementInfo.remittance_details.remittance_office_id_fk
            this.form.remittance_commission = this.statementInfo.remittance_details.remittance_commission
          }
        }
      }
    }
  },

  methods: {
    ...mapActions({
      updateStatement: 'receivableStatements/updateStatementInfo'
    }),

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.editDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    amountCalculation() {
      const exchangeSecondary =
        this.systemCurrency === 'USD'
          ? Number(this.form.amount_secondary) / this.form.dollar_rate
          : Number(this.form.amount_secondary) * this.form.dollar_rate
      this.form.amount = (Number(this.form.amount_main) + exchangeSecondary).toFixed(2)
    },

    validateBeforeSubmit(event) {
      this.$validator
        .validateAll()
        .then(() => {
          if (!this.errors.any()) {
            this.btnUpdateLoading = true

            let formData = new FormData(event.target)
            formData.append('is_multi_currency', this.isMultiCurrency ? '1' : '0')
            formData.append('dollar_rate', this.form.dollar_rate)
            formData.append('account_receivable_id_fk', this.accountId)
            formData.append('cash_or_opening_balance', this.form.payment_type)
            formData.append('remittance_office_id_fk', this.form.remittance_office_id_fk || '')

            // for (let pair of formData.entries()) {
            //   console.warn(`${pair[0]}: ${pair[1]}`)
            // }

            const payload = { id: this.form.statement_id, formData }
            this.updateStatement(payload)
              .then(() => {
                this.btnUpdateLoading = false
                this.$emit('reset-datatable')
                this.$emit('hidemodal')
              })
              .catch(() => {
                this.btnUpdateLoading = false
              })
          }
        })
        .catch(() => {
          this.btnUpdateLoading = false
        })
    }
  }
}
</script>

<style scoped />
